import { readSessionActiveOrgId } from '@/lib/utils/activeOrganisation';
import { handleSignOut } from '@/lib/utils/auth';
import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

type CreateApolloClientOptions = {
  accessToken?: string;
  organisationId?: string;
};

type GraphQLErrorLike = {
  message?: string;
  extensions?: {
    code?: string;
  };
};

type NetworkErrorWithResult = Error & {
  result?: {
    errors?: GraphQLErrorLike[];
  };
  statusCode?: number;
};

const isNotAuthenticatedGraphQLError = (error: GraphQLErrorLike): boolean => {
  return error.message === 'Not authenticated' && error.extensions?.code === 'NOT_AUTHENTICATED';
};

const getNetworkGraphQLErrors = (networkError: unknown): GraphQLErrorLike[] => {
  const result = (networkError as NetworkErrorWithResult | null)?.result;

  return Array.isArray(result?.errors) ? result.errors : [];
};

const isAuthenticationNetworkError = (networkError: unknown): boolean => {
  const serverError = networkError as NetworkErrorWithResult | null;

  if (serverError?.statusCode === 401) {
    return true;
  }

  return getNetworkGraphQLErrors(networkError).some(isNotAuthenticatedGraphQLError);
};

const getAuthFailureCallbackUrl = (): string => {
  const callbackUrl = new URL('/login', window.location.origin);

  callbackUrl.searchParams.set('autoSignIn', 'false');

  return callbackUrl.toString();
};

export const createApolloClient = ({ accessToken = '', organisationId = '' }: CreateApolloClientOptions) => {
  const httpLink = createHttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URL || 'http://localhost:4000',
  });

  const authLink = setContext((_, { headers }) => {
    const currentOrganisationId =
      typeof window !== 'undefined' ? (readSessionActiveOrgId() ?? organisationId) : organisationId;

    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : '',
        'x-organisation-id': currentOrganisationId,
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const hasNotAuthenticatedGraphQLError = graphQLErrors?.some(isNotAuthenticatedGraphQLError) ?? false;
    const hasAuthenticationNetworkError = isAuthenticationNetworkError(networkError);

    if ((hasNotAuthenticatedGraphQLError || hasAuthenticationNetworkError) && typeof window !== 'undefined') {
      console.error('GraphQL authentication failure', {
        operationName: operation.operationName,
        graphQLErrorCount: graphQLErrors?.length ?? 0,
        statusCode: (networkError as NetworkErrorWithResult | null)?.statusCode,
      });

      void handleSignOut({
        callbackUrl: getAuthFailureCallbackUrl(),
      });

      return;
    }

    if (graphQLErrors != null && graphQLErrors.length > 0) {
      console.error('GraphQL errors', {
        operationName: operation.operationName,
        errors: graphQLErrors.map((error) => ({
          message: error.message,
          code: error.extensions?.code,
          path: error.path,
        })),
      });
    }

    if (networkError != null) {
      console.error('GraphQL network error', {
        operationName: operation.operationName,
        name: networkError.name,
        message: networkError.message,
        statusCode: (networkError as NetworkErrorWithResult | null)?.statusCode,
      });
    }
  });

  return new ApolloClient({
    link: errorLink.concat(authLink).concat(httpLink),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};
